import React, { useEffect, useState } from "react";
import api, { formatINR } from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Receipt, FileText, ExternalLink, Trash2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useAuth } from "../../contexts/AuthContext";

const STATUS_BADGE = {
    pending: "bg-amber-50 text-amber-800",
    approved: "bg-sky-50 text-sky-800",
    rejected: "bg-red-50 text-red-800",
    paid: "bg-emerald-50 text-emerald-800",
};
const NEXT_ACTIONS = ["pending", "approved", "rejected", "paid"];

/**
 * Used in BOTH /admin/mr-expenses (admin/editor) and /employee/accounts/mr-expenses (accounts staff).
 * Lists all expenses across MRs with a status filter; one-click status updates.
 */
export default function AdminMRExpenses() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [status, setStatus] = useState("all");
    const [confirmDelete, setConfirmDelete] = useState(null);

    const canDelete = user?.role === "admin" || user?.role === "editor";

    const load = async () => {
        try {
            const params = status !== "all" ? { status } : {};
            const { data } = await api.get("/admin/mr-expenses", { params });
            setItems(data);
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

    const review = async (exp, newStatus) => {
        try {
            await api.put(`/admin/mr-expenses/${exp.id}/review`, { status: newStatus });
            toast.success(`Marked ${newStatus}`);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const doDelete = async () => {
        if (!confirmDelete) return;
        try {
            await api.delete(`/mr/expenses/${confirmDelete.id}`);
            toast.success("Expense deleted");
            setConfirmDelete(null);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const apiBase = (api.defaults.baseURL || "").replace(/\/api$/, "");

    return (
        <div data-testid="admin-mr-expenses">
            <Toaster position="top-center" />
            <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                <div>
                    <div className="label-eyebrow text-sky-700">Approvals</div>
                    <h1 className="font-serif text-4xl text-slate-900 mt-2">MR Market Expenses</h1>
                    <p className="text-sm text-stone-600 mt-2">Review receipts uploaded by Medical Reps and mark them approved / paid.</p>
                </div>
                <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="rounded-none border-stone-300 w-44" data-testid="filter-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        {NEXT_ACTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>

            <div className="bg-white border border-sky-100 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead><tr className="bg-sky-50 border-b border-sky-100">
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Date</th>
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">MR</th>
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Category</th>
                        <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Amount</th>
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Description</th>
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Receipt</th>
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Status</th>
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Action</th>
                    </tr></thead>
                    <tbody>
                        {items.length === 0 ? (
                            <tr><td colSpan={8} className="p-10 text-center text-stone-500">No expenses match.</td></tr>
                        ) : items.map((e) => (
                            <tr key={e.id} className="border-b border-stone-100" data-testid={`amr-row-${e.id}`}>
                                <td className="p-3 whitespace-nowrap">{e.expense_date}</td>
                                <td className="p-3"><div className="font-medium">{e.user_name || "—"}</div><div className="text-[11px] text-stone-500">{e.user_email}</div></td>
                                <td className="p-3 capitalize text-xs">{e.category}</td>
                                <td className="p-3 text-right font-mono">{formatINR(e.amount)}</td>
                                <td className="p-3 text-stone-600 max-w-xs truncate">{e.description || "—"}</td>
                                <td className="p-3">
                                    {e.receipt_url ? (
                                        <a href={`${apiBase}${e.receipt_url}`} target="_blank" rel="noreferrer" className="text-sky-700 hover:text-sky-800 underline inline-flex items-center text-xs">
                                            <FileText className="w-3.5 h-3.5 mr-1" />View<ExternalLink className="w-3 h-3 ml-1" />
                                        </a>
                                    ) : <span className="text-stone-400 text-xs">—</span>}
                                </td>
                                <td className="p-3">
                                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${STATUS_BADGE[e.status]}`}>{e.status}</span>
                                </td>
                                <td className="p-3">
                                    <div className="flex flex-wrap gap-1">
                                        {e.status !== "approved" && <Button size="sm" variant="outline" onClick={() => review(e, "approved")} className="rounded-none border-sky-700 text-sky-700 text-xs h-7" data-testid={`amr-approve-${e.id}`}>Approve</Button>}
                                        {e.status !== "rejected" && <Button size="sm" variant="outline" onClick={() => review(e, "rejected")} className="rounded-none border-red-700 text-red-700 text-xs h-7" data-testid={`amr-reject-${e.id}`}>Reject</Button>}
                                        {e.status !== "paid" && <Button size="sm" onClick={() => review(e, "paid")} className="rounded-none bg-emerald-700 hover:bg-emerald-800 text-white text-xs h-7" data-testid={`amr-pay-${e.id}`}><Receipt className="w-3 h-3 mr-1" />Mark paid</Button>}
                                        {canDelete && (
                                            <Button size="sm" variant="outline" onClick={() => setConfirmDelete(e)} className="rounded-none border-red-300 text-red-700 hover:bg-red-50 text-xs h-7" data-testid={`amr-delete-${e.id}`} title="Delete expense">
                                                <Trash2 className="w-3 h-3" />
                                            </Button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent className="rounded-none">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this expense?</AlertDialogTitle>
                        <AlertDialogDescription>
                            <strong>{confirmDelete?.category}</strong> · <span className="font-mono">{formatINR(confirmDelete?.amount)}</span> by {confirmDelete?.user_email || "MR"} (status: {confirmDelete?.status}) will be permanently removed.
                            <span className="block mt-2 text-xs">This action is logged for admins.</span>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="amr-delete-cancel">Keep</AlertDialogCancel>
                        <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700 rounded-none" data-testid="amr-delete-confirm">
                            Delete expense
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
